import '../../styles/components/admin.css'
import { requireAuth } from '../../lib/supabase.js'
import { formatPrice } from '../../utils/helpers.js'

const STRIPE_KEY_URL = '/api/stripe-proxy'

export function renderAdminPromoCodes(outlet) {
  requireAuth(async () => {
    outlet.innerHTML = `
      <div class="admin-page">
        <div class="container">
          <div class="admin-topbar" style="margin-bottom: var(--space-6);">
            <h1 class="admin-title">Promo Codes</h1>
            <a href="#/admin" class="admin-back">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
              Back to Products
            </a>
          </div>

          <div id="promo-error" class="admin-error" style="display:none; margin-bottom: var(--space-4);"></div>
          <div id="promo-success" class="admin-success" style="display:none; margin-bottom: var(--space-4);"></div>

          <!-- Create form -->
          <div class="card" style="padding: var(--space-6); margin-bottom: var(--space-6);">
            <h2 style="font-size: var(--text-lg); font-weight: var(--font-semibold); margin-bottom: var(--space-5);">Create Promo Code</h2>
            <form id="promo-create-form" novalidate>
              <div class="admin-form-row" style="grid-template-columns: 1fr 1fr;">
                <div class="form-group">
                  <label class="form-label" for="promo-code">Code <span style="color:var(--color-error)">*</span></label>
                  <input id="promo-code" class="form-input" type="text" placeholder="e.g. SAVE20" style="text-transform:uppercase;" required />
                </div>
                <div class="form-group">
                  <label class="form-label" for="promo-type">Discount Type <span style="color:var(--color-error)">*</span></label>
                  <select id="promo-type" class="form-input">
                    <option value="percent">Percent off (%)</option>
                    <option value="amount">Amount off ($)</option>
                  </select>
                </div>
              </div>
              <div class="admin-form-row" style="grid-template-columns: 1fr 1fr 1fr;">
                <div class="form-group">
                  <label class="form-label" for="promo-value">Value <span style="color:var(--color-error)">*</span></label>
                  <input id="promo-value" class="form-input" type="number" min="1" step="1" placeholder="20" required />
                </div>
                <div class="form-group">
                  <label class="form-label" for="promo-max">Max Redemptions</label>
                  <input id="promo-max" class="form-input" type="number" min="1" step="1" placeholder="Unlimited" />
                </div>
                <div class="form-group">
                  <label class="form-label" for="promo-expiry">Expires</label>
                  <input id="promo-expiry" class="form-input" type="date" />
                </div>
              </div>
              <button type="submit" class="btn btn-primary" id="promo-submit-btn">Create Code</button>
            </form>
          </div>

          <!-- List -->
          <div id="promo-list-content">
            <div class="products-loading" style="padding: var(--space-10) 0; text-align:center;">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    `

    await loadPromoCodes()

    document.getElementById('promo-code').addEventListener('input', function () {
      this.value = this.value.toUpperCase()
    })

    document.getElementById('promo-create-form').addEventListener('submit', async (e) => {
      e.preventDefault()
      hideMessages()

      const code = document.getElementById('promo-code').value.trim().toUpperCase()
      const type = document.getElementById('promo-type').value
      const value = parseFloat(document.getElementById('promo-value').value)
      const max = parseInt(document.getElementById('promo-max').value, 10)
      const expiry = document.getElementById('promo-expiry').value

      if (!code) return showError('Code is required.')
      if (isNaN(value) || value <= 0) return showError('Enter a valid discount value.')
      if (type === 'percent' && value > 100) return showError('Percent off cannot exceed 100.')

      const btn = document.getElementById('promo-submit-btn')
      btn.disabled = true
      btn.textContent = 'Creating…'

      try {
        await createPromoCode({ code, type, value, max, expiry })
        showSuccess(`Promo code "${code}" created.`)
        e.target.reset()
        await loadPromoCodes()
      } catch (err) {
        showError(err.message || 'Failed to create promo code.')
      } finally {
        btn.disabled = false
        btn.textContent = 'Create Code'
      }
    })
  })
}

async function loadPromoCodes() {
  const content = document.getElementById('promo-list-content')
  if (!content) return

  try {
    const res = await fetch('/api/stripe-promo?action=list')
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load promo codes')

    const codes = data.data || []

    if (codes.length === 0) {
      content.innerHTML = `<p style="color: var(--color-text-secondary); text-align:center; padding: var(--space-8);">No promo codes yet.</p>`
      return
    }

    content.innerHTML = `
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Redemptions</th>
              <th>Expires</th>
              <th>Status</th>
              <th style="width:100px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${codes.map(renderPromoRow).join('')}
          </tbody>
        </table>
      </div>
    `

    content.querySelectorAll('[data-deactivate]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.deactivate
        const code = btn.dataset.code
        if (!confirm(`Deactivate "${code}"?`)) return
        btn.disabled = true
        btn.textContent = '…'
        try {
          await deactivatePromoCode(id)
          showSuccess(`"${code}" deactivated.`)
          await loadPromoCodes()
        } catch (err) {
          showError(err.message || 'Failed to deactivate.')
          btn.disabled = false
          btn.textContent = 'Deactivate'
        }
      })
    })

  } catch (err) {
    content.innerHTML = `<div class="admin-error">${err.message}</div>`
  }
}

function renderPromoRow(p) {
  const coupon = p.promotion?.coupon || p.coupon || {}
  const discount = coupon.percent_off
    ? `${coupon.percent_off}% off`
    : coupon.amount_off
      ? `${formatPrice(coupon.amount_off)} off`
      : '—'
  const redemptions = p.times_redeemed != null
    ? `${p.times_redeemed}${p.max_redemptions ? ` / ${p.max_redemptions}` : ''}`
    : '—'
  const expires = p.expires_at
    ? new Date(p.expires_at * 1000).toLocaleDateString()
    : 'Never'
  const active = p.active

  return `
    <tr>
      <td><code style="font-family:monospace; font-size:var(--text-sm);">${p.code}</code></td>
      <td>${discount}</td>
      <td>${redemptions}</td>
      <td>${expires}</td>
      <td>
        <span class="admin-badge ${active ? 'admin-badge-active' : 'admin-badge-inactive'}">
          ${active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td class="cell-actions">
        ${active ? `<button class="btn btn-sm" style="font-size:var(--text-xs); border-color:var(--color-error); color:var(--color-error);"
          data-deactivate="${p.id}" data-code="${p.code}">Deactivate</button>` : '—'}
      </td>
    </tr>
  `
}

async function createPromoCode({ code, type, value, max, expiry }) {
  const res = await fetch('/api/stripe-promo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create', code, type, value, max: max || null, expiry: expiry || null }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to create promo code')
  return data
}

async function deactivatePromoCode(id) {
  const res = await fetch('/api/stripe-promo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'deactivate', id }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to deactivate')
  return data
}

function showError(msg) {
  const el = document.getElementById('promo-error')
  if (!el) return
  el.textContent = msg
  el.style.display = 'block'
}

function showSuccess(msg) {
  const el = document.getElementById('promo-success')
  if (!el) return
  el.textContent = msg
  el.style.display = 'block'
}

function hideMessages() {
  document.getElementById('promo-error').style.display = 'none'
  document.getElementById('promo-success').style.display = 'none'
}
